// WHAT: Widget component for displaying Miracle Flow session status and statistics
// WHY:  Provides dashboard overview of Miracle Flow activity with streak and session count
// DOMAIN: Core → Dashboard → Widgets → Productivity
// DEPENDENCIES: SessionStatusIndicator, useDashboardStats hook
// RISKS:
//   - NOTE: Stats may not update in real-time
//   - NOTE: Loading state may flash on mount
//   - NOTE: Default size may not match user preferences

import { Target } from "lucide-react";
import { type FC } from "react";

import { Text, Heading, Card, Grid } from "@/components/ui";
import { SessionStatusIndicator } from "@/domains/core/dashboard/components/SessionStatusIndicator";
import { useDashboardStats } from "@/domains/productivity/miracle-flow/hooks/useDashboardStats";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base";

/**
 * WHAT:
 *   Widget component that displays Miracle Flow session status and key
 *   statistics (streak, total sessions).
 *
 * WHY:
 *   Enables users to see their Miracle Flow activity at a glance on the dashboard.
 *
 * BEHAVIOR:
 *   - Displays session status indicator
 *   - Shows current streak count
 *   - Shows total sessions count
 *   - Handles loading state
 *
 * RISKS:
 *   - NOTE: Stats may not update in real-time
 *   - NOTE: Loading state may flash on mount
 *   - NOTE: Default size may not match user preferences
 *
 * @returns {JSX.Element} MiracleFlow status widget component
 *
 */
export const MiracleFlowStatusWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.MIRACLEFLOW_STATUS);
  const { data: stats, isLoading } = useDashboardStats();

  return (
    <WidgetContainer
      description="Session status & stats"
      icon={<Target className="w-5 h-5" />}
      loading={isLoading}
      size={config?.size ?? "small"}
      title="MiracleFlow"
      widgetId={WIDGET_IDS.MIRACLEFLOW_STATUS}
    >
      <div className="space-y-4">
        <SessionStatusIndicator />

        {stats ? (
          <Grid cols={{ sm: 2 }} gap={2}>
            <Card className="text-center" padding="sm" variant="default">
              <Text className="text-xs text-gray-400 mb-1">Streak</Text>
              <Heading className="text-lg font-bold text-white" level={4}>
                {stats.current_streak || 0}
              </Heading>
            </Card>

            <Card className="text-center" padding="sm" variant="default">
              <Text className="text-xs text-gray-400 mb-1">Sessions</Text>
              <Heading className="text-lg font-bold text-white" level={4}>
                {stats.total_sessions || 0}
              </Heading>
            </Card>
          </Grid>
        ) : null}
      </div>
    </WidgetContainer>
  );
};
